import type { CompanySettings, RouteRow, ScheduleAssignmentRow, ScheduleDayRow } from '@routeback/core';
import { addDays, weekday } from '../timeoff/timeoff';

/**
 * Schedule — pure logic for the driver's week and the team grid. Dates are
 * `YYYY-MM-DD`; weeks start on the company's day (Saturday by default, like
 * the Driver Portal's Sat–Fri week).
 */

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = DAY_NAMES.map((d) => d.slice(0, 3));

/** One person on the team schedule (from `schedule_roster`). */
export interface RosterEntry {
  user_id: string;
  display_name: string;
  /** false = left off the schedule builder (office staff, long leave). */
  on_schedule: boolean;
  /** Days they can't work (approved time off or "can't work") — never the reason. */
  off_dates: string[];
}

export function weekStartDay(settings: CompanySettings): number {
  const v = settings['schedule_week_start'];
  return typeof v === 'number' && v >= 0 && v <= 6 ? v : 6;
}

/** The first day of the week containing `date`. */
export function weekStartOf(date: string, startDow: number): string {
  return addDays(date, -((weekday(date) - startDow + 7) % 7));
}

export const weekDates = (start: string): string[] => Array.from({ length: 7 }, (_, i) => addDays(start, i));

/** "Mike Raitano" → "Mike R." (fits a grid cell; the full name shows on tap). */
export function shortName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? '';
  return `${parts[0]} ${parts[parts.length - 1]![0]!.toUpperCase()}.`;
}

/** Routes in display order: sort_order, then numeric code (512 before 1111), then text. */
export function sortRoutes(routes: RouteRow[]): RouteRow[] {
  return [...routes].sort((a, b) => a.sort_order - b.sort_order || (parseInt(a.code, 10) || 0) - (parseInt(b.code, 10) || 0) || a.code.localeCompare(b.code));
}

export interface DayInfo {
  /** false when the route (or the whole company) is switched off that day. */
  open: boolean;
  /** Route note first, then the company's note for the day. */
  notes: string[];
}

/** Is this route running on this day, and what notes go with it? */
export function dayInfo(days: ScheduleDayRow[], date: string, routeId: string | null): DayInfo {
  const company = days.find((d) => !d.deleted_at && d.work_date === date && !d.route_id);
  const route = routeId ? days.find((d) => !d.deleted_at && d.work_date === date && d.route_id === routeId) : undefined;
  const open = (company?.open ?? true) && (route?.open ?? true);
  const notes = [route?.note, company?.note].map((n) => (n ?? '').trim()).filter(Boolean);
  return { open, notes };
}

/** The assignment for a route on a day (newest wins if a manager double-booked it). */
export function assignmentFor(rows: ScheduleAssignmentRow[], routeId: string, date: string): ScheduleAssignmentRow | undefined {
  return rows
    .filter((a) => !a.deleted_at && a.route_id === routeId && a.work_date === date)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
}

export type MyDay =
  | { kind: 'work'; date: string; assignments: ScheduleAssignmentRow[] }
  | { kind: 'timeoff'; date: string }
  | { kind: 'none'; date: string };

/** My seven days: routes I'm on, time off, or nothing scheduled. */
export function myWeek(dates: string[], rows: ScheduleAssignmentRow[], me: string, myOff: string[]): MyDay[] {
  return dates.map((date) => {
    const mine = rows.filter((a) => !a.deleted_at && a.user_id === me && a.work_date === date);
    if (mine.length) return { kind: 'work', date, assignments: mine };
    if (myOff.includes(date)) return { kind: 'timeoff', date };
    return { kind: 'none', date };
  });
}

/** The plugin's "Available drivers by day": on the schedule, not assigned, not off. */
export function availableByDay(dates: string[], roster: RosterEntry[], rows: ScheduleAssignmentRow[]): Record<string, RosterEntry[]> {
  const out: Record<string, RosterEntry[]> = {};
  for (const date of dates) {
    const busy = new Set(rows.filter((a) => !a.deleted_at && a.work_date === date && a.user_id).map((a) => a.user_id));
    out[date] = roster.filter((r) => r.on_schedule && !busy.has(r.user_id) && !r.off_dates.includes(date));
  }
  return out;
}

/** Show a route/assignment under the chosen terminal ('all' shows everything; unassigned terminals always show). */
export const inTerminal = (terminalId: string | null | undefined, choice: string): boolean => choice === 'all' || !terminalId || terminalId === choice;

/**
 * Which van line to open: the one the caller picked last time (if it still
 * exists), else the line my truck is parked on today, else the first.
 */
export function pickLine(lineIds: string[], saved: string | null | undefined, myLine: string | null | undefined): string | null {
  if (!lineIds.length) return null;
  if (saved && lineIds.includes(saved)) return saved;
  if (myLine && lineIds.includes(myLine)) return myLine;
  return lineIds[0]!;
}
